import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Check, Copy, Link2, Loader2, Trash2, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/misc";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Hint } from "@/components/ui/tooltip";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasRole, ROLE_LABELS, type InvitableRole, type Invite, type User } from "@/lib/types";

const ROLE_HELP: Record<InvitableRole, string> = {
  admin: "Everything but the owner's account: members, invites, projects",
  member: "Read and change boards, cards and comments",
  viewer: "Read only",
};

/**
 * Who can use the board, and the way in for anyone new — the Members section of
 * Settings (it was a dialog once; the file keeps its name).
 *
 * The board cannot send mail, so an invite is a link: made here, copied, and passed
 * on by whoever made it. The link is shown once — only a hash of its token is kept,
 * so it cannot be shown again, only replaced by a fresh invite. Settings shows this
 * to admins only; every write here is `requireRole("admin")` on the server anyway.
 */
export function MembersPanel() {
  const { user: me } = useAuth();
  const isAdmin = hasRole(me, "admin");
  const [members, setMembers] = useState<User[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<InvitableRole>("member");
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState<{ email: string; url: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    const [{ members: list }, pending] = await Promise.all([
      api.members(),
      isAdmin ? api.invites().then((result) => result.invites) : Promise.resolve([] as Invite[]),
    ]);
    setMembers(list);
    setInvites(pending.filter((invite) => invite.status === "pending"));
  }, [isAdmin]);

  useEffect(() => {
    void load().catch((error) => toast.error(error instanceof ApiError ? error.message : "Could not load members"));
  }, [load]);

  const fail = (error: unknown, fallback: string) => toast.error(error instanceof ApiError ? error.message : fallback);

  const invite = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const { token, invite: created } = await api.createInvite({ email, role });
      const url = new URL(window.location.origin);
      url.searchParams.set("invite", token);
      setLink({ email: created.email, url: url.toString() });
      setCopied(false);
      setEmail("");
      await load();
    } catch (error) {
      fail(error, "Could not create the invite");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!link) return;
    await navigator.clipboard.writeText(link.url);
    setCopied(true);
  };

  const changeRole = async (member: User, next: InvitableRole) => {
    try {
      await api.updateMember(member.id, next);
      toast.success(`${member.displayName} is now ${ROLE_LABELS[next].toLowerCase()}`);
      await load();
    } catch (error) {
      fail(error, "Could not change the role");
    }
  };

  const remove = async (member: User) => {
    if (!window.confirm(`Remove ${member.displayName}? They are signed out everywhere and cannot sign in again. Their cards and comments stay.`)) return;
    try {
      await api.removeMember(member.id);
      toast.success(`${member.displayName} removed`);
      await load();
    } catch (error) {
      fail(error, "Could not remove the member");
    }
  };

  const revoke = async (entry: Invite) => {
    try {
      await api.revokeInvite(entry.id);
      await load();
    } catch (error) {
      fail(error, "Could not revoke the invite");
    }
  };

  /** Mirrors the server's rule, so only controls that would work are offered. */
  const canManage = (member: User) =>
    isAdmin && member.id !== me.id && member.role !== "owner" && !member.disabled && (me.role === "owner" || member.role !== "admin");

  const active = members.filter((member) => !member.disabled);

  return (
    <div className="space-y-4">
        <ul className="space-y-1.5">
          {active.map((member) => (
            <li key={member.id} className="flex items-center gap-2 rounded-md border border-border/60 px-2.5 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-medium">
                  {member.displayName}
                  {member.id === me.id ? <span className="font-normal text-muted-foreground"> (you)</span> : null}
                </p>
                <p className="truncate text-[11.5px] text-muted-foreground">{member.email}</p>
              </div>
              {canManage(member) ? (
                <Select value={member.role ?? "member"} onValueChange={(next) => void changeRole(member, next as InvitableRole)}>
                  <SelectTrigger className="h-7 w-28 text-[12px]" aria-label={`Role for ${member.displayName}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(["admin", "member", "viewer"] as const)
                      .filter((option) => option !== "admin" || me.role === "owner")
                      .map((option) => (
                        <SelectItem key={option} value={option}>
                          {ROLE_LABELS[option]}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              ) : (
                <Badge variant="outline">{member.role ? ROLE_LABELS[member.role] : "—"}</Badge>
              )}
              {canManage(member) ? (
                <Hint label="Remove access">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 text-muted-foreground hover:text-destructive"
                    onClick={() => void remove(member)}
                    aria-label={`Remove ${member.displayName}`}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </Hint>
              ) : null}
            </li>
          ))}
        </ul>

        {isAdmin ? (
          <>
            <Separator />
            <form onSubmit={invite} className="space-y-2">
              <Label htmlFor="invite-email">Invite someone</Label>
              <div className="flex gap-2">
                <Input
                  id="invite-email"
                  type="email"
                  required
                  placeholder="name@example.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
                <Select value={role} onValueChange={(next) => setRole(next as InvitableRole)}>
                  <SelectTrigger className="w-28" aria-label="Role for the invite">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(["admin", "member", "viewer"] as const)
                      .filter((option) => option !== "admin" || me.role === "owner")
                      .map((option) => (
                        <SelectItem key={option} value={option}>
                          {ROLE_LABELS[option]}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <Button type="submit" disabled={busy}>
                  {busy ? <Loader2 className="animate-spin" /> : <UserPlus />} Invite
                </Button>
              </div>
              <p className="text-[11.5px] text-muted-foreground">{ROLE_HELP[role]}.</p>
            </form>

            {link ? (
              <div className="space-y-1.5 rounded-md border border-primary/40 bg-primary/5 p-2.5">
                <p className="flex items-center gap-1.5 text-[12.5px] font-medium">
                  <Link2 className="size-3.5" /> Invite link for {link.email}
                </p>
                <div className="flex gap-2">
                  <Input readOnly value={link.url} onFocus={(event) => event.currentTarget.select()} className="font-mono text-[11px]" />
                  <Button type="button" variant="outline" onClick={() => void copy()}>
                    {copied ? <Check /> : <Copy />} {copied ? "Copied" : "Copy"}
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Shown once — send it to them yourself. It works for 7 days and only once.
                </p>
              </div>
            ) : null}

            {invites.length > 0 ? (
              <div className="space-y-1.5">
                <p className="text-[12px] font-medium text-muted-foreground">Waiting to join</p>
                <ul className="space-y-1">
                  {invites.map((entry) => (
                    <li key={entry.id} className="flex items-center gap-2 text-[12.5px]">
                      <span className="min-w-0 flex-1 truncate">{entry.email}</span>
                      <Badge variant="outline">{ROLE_LABELS[entry.role]}</Badge>
                      <Hint label="Revoke this invite">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-6 text-muted-foreground hover:text-destructive"
                          onClick={() => void revoke(entry)}
                          aria-label={`Revoke invite for ${entry.email}`}
                        >
                          <X className="size-3.5" />
                        </Button>
                      </Hint>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        ) : null}
    </div>
  );
}
